'use client';

import { useEffect, useMemo, useRef } from 'react';
import {
  infiniteQueryOptions,
  useInfiniteQuery,
  type DefaultError,
  type InfiniteData
} from '@tanstack/react-query';
import type { PaginatedResponse } from '@/lib/api/types';

/**
 * Query options produced by a feature's `*.infiniteList(filters)` factory — the
 * `infiniteQueryOptions(...)` result over a `PaginatedResponse<TItem>` page,
 * page-cursored by a numeric `page` param. The query-key generic is left as
 * `any`: it sits in a contravariant position (`enabled`'s `Query` callback), so
 * pinning it to a broad `QueryKey` would reject each caller's specific key
 * tuple — `any` lets every feature's key through while keeping `TItem` precise.
 */
type InfiniteListOptions<TItem> = ReturnType<
  typeof infiniteQueryOptions<
    PaginatedResponse<TItem>,
    DefaultError,
    InfiniteData<PaginatedResponse<TItem>>,
    any,
    number
  >
>;

/**
 * Drives a paginated list with load-on-scroll. Pass a feature's
 * `*.infiniteList(filters)` query options; the hook runs the infinite query,
 * flattens the pages into a single `items` array, and returns a `loadMoreRef`
 * to drop at the end of the scroll container — an IntersectionObserver fetches
 * the next page as that sentinel scrolls into view.
 *
 * Replaces the hand-rolled useInfiniteQuery + flatten + observer block that was
 * duplicated across the selection panels. The caller still owns its own search
 * input / debounce and folds the term into the `filters` it passes here.
 */
function useInfiniteScroll<TItem>(options: InfiniteListOptions<TItem>) {
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery(options);

  const items = useMemo<TItem[]>(
    () => data?.pages.flatMap((page) => page.data ?? []) ?? [],
    [data]
  );

  const loadMoreRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    items,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    loadMoreRef
  };
}

export { useInfiniteScroll };
